/**
 * Las etiquetas de evidencia, en inglés y en frase.
 *
 * POR QUÉ EXISTE ESTO. Las etiquetas son identificadores internos —
 * `6:anticipa-peligro`— y la pantalla de resultado las imprimía EN CRUDO.
 * O sea que a alguien que hace el test en inglés se le devolvía, como
 * justificación de su resultado, una lista de slugs en español. La sección se
 * llama «Evidence» y es el punto entero del producto: es lo que hace que el
 * resultado sea discutible en vez de un veredicto. Ilegible, no vale para nada.
 *
 * Se detectó recorriendo la app en el navegador después de enchufar el
 * contenido real. No lo cazó ninguna prueba: `ui-language.test.ts` comprueba el
 * texto de las opciones, y las etiquetas no son texto de opción hasta que la
 * pantalla las enseña.
 *
 * ESTO ES COPY, NO SCORING. El `DEFINITION_OF_DONE` reserva al autor los pesos;
 * traducir una etiqueta no cambia ninguna puntuación — la misma respuesta
 * produce el mismo resultado con esta tabla y sin ella. Sólo cambia si se
 * entiende.
 *
 * CÓMO ESTÁN ESCRITAS. En segunda persona y describiendo LA RESPUESTA QUE DIO
 * la persona, no un rasgo suyo. «You went back over it» es un hecho que
 * cualquiera puede confirmar o negar; «You are a perfectionist» es una etiqueta
 * de identidad, que es exactamente lo que la primera pantalla promete no hacer.
 * La contraevidencia se lee igual: es lo que NO encajaba.
 */

/** Etiqueta → frase. La guarda de `content.test.ts` exige que estén todas. */
export const EVIDENCE_LABELS: Readonly<Record<string, string>> = {
  // ── 1 ──────────────────────────────────────────────────────────────────
  "1:esta-bien-hecho": "You check whether it is right, not whether it is risky",
  "1:estandar-independiente-de-audiencia":
    "Your standard holds whether or not anyone is watching",
  "1:no-cierra-tareas": "You go back over things that are already finished",
  "1:tolera-error-propio": "You let something imperfect stand without it bothering you",

  // ── 6 ──────────────────────────────────────────────────────────────────
  "6:anticipa-peligro": "You look ahead for what could go wrong",
  "6:busca-certeza": "You wait for something that would make the choice safe",
  "6:consulta-repetida": "You ask more than one person about the same thing",
  "6:decide-sin-validar": "You decided without needing anyone to confirm it",
  "6:es-seguro": "You check for exposure rather than for quality",

  // ── 9 ──────────────────────────────────────────────────────────────────
  "9:aplaza": "You put the decision off until it stopped feeling urgent",
  "9:cede-sin-decidir": "You agreed before you had decided what you wanted",
  "9:da-igual": "You found the difference between the options not worth the effort",
  "9:evita-conflicto": "You stayed quiet rather than hold a position",
  "9:nombra-preferencia": "You said what you would prefer, even though it was awkward",

  // ── Los seis tipos que faltaban, y las señales nuevas del 1 y el 9 ─────
  // Mismo criterio: describen LA RESPUESTA que se dio, no un rasgo. La guarda
  // de `content.test.ts` prohíbe «you are» por eso.
  "1:enfado-contenido": "You hold the anger in, and some of it turns on you",
  "2:ayuda-no-solicitada": "You stepped in before anyone asked you to",
  "2:ayuda-por-vinculo": "What decided it was who the person is to you",
  "2:foco-en-vinculo": "What you would mind losing is the closeness",
  "2:pide-y-limita-sin-culpa": "You say no, and ask for help, without needing to justify it",
  "3:exigencia-decae-sin-audiencia": "The effort drops when nobody will be looking",
  "3:foco-en-logro": "What you would mind losing is the track record",
  "3:reformula-fracaso": "You find it hard to leave a failure standing as a failure",
  "3:sostiene-mala-imagen": "You let an unflattering account of yourself stand",
  "4:comparacion-carencia": "Comparing yourself leaves you feeling something is missing",
  "4:disfruta-lo-ordinario": "You enjoy ordinary things without needing them to be more",
  "4:intensidad": "Where you go when you withdraw is full of feeling",
  "4:teme-lo-corriente": "What you would mind is being seen as interchangeable",
  "5:incomoda-lo-emocional-directo": "A direct emotional question catches you unprepared",
  "5:prepara-en-soledad": "You get ready on your own rather than by talking it through",
  "5:repliegue-racional": "Where you go when you withdraw is quiet and analytical",
  "5:se-aisla": "You step out of the conflict without taking any side",
  "5:se-compromete-sin-preparar": "You answer properly on the spot, unprepared",
  "7:busca-mejor-opcion": "Your attention runs on what else you could be doing",
  "7:desvia-el-malestar": "You lighten a painful conversation or move it on",
  "7:evita-compromiso": "You keep it vague and leave yourself a way out",
  "7:permanece-en-lo-dificil": "You stayed in something uncomfortable without needing a way out",
  "8:cede-control-sin-derrota": "You can hand over control without it costing you anything",
  "8:confronta-directo": "You push back there and then rather than let it sit",
  "8:enfado-hacia-fuera": "The anger goes outward, and quickly",
  "8:reacciona-al-control": "Being told what to do provokes something immediate in you",
  "9:enfado-contenido": "You say nothing, and it comes out sideways later",

  // Señales COMPARTIDAS: la misma respuesta sostiene dos hipótesis a la vez, y
  // por eso pesa +1 a cada una. Se muestran las dos etiquetas: enseñar sólo una
  // haría el resultado parecer más limpio de lo que es.
  "7:reencuadra-lo-doloroso": "You turn what hurt into a lesson rather than let it sit",
  "3:eficacia-sobre-permanencia": "You moved it towards a decision rather than staying in it",
  "8:toma-el-mando": "You took charge of it",
  "2:no-nombra-lo-propio": "Asked what you need, you couldn't name it",
  "4:lenguaje-intenso": "You describe ordinary things in vivid terms",
  "5:investiga-antes-de-actuar": "You find out about it properly before doing anything",
};

/**
 * La frase, o la etiqueta tal cual si falta. NO se lanza: una etiqueta sin
 * traducir es un fallo de contenido —lo caza la guarda en las pruebas— pero no
 * es motivo para tumbarle el resultado a quien está usando la app.
 */
export function labelFor(tag: string): string {
  return EVIDENCE_LABELS[tag] ?? tag;
}
